import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Search, Link2, AlertCircle, Info } from "lucide-react";
import { apiClient } from "../lib/api";
import type { CreateAnalysisInput } from "@erc/shared";

export default function NewAnalysisPage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (data: CreateAnalysisInput) => apiClient.createAnalysis(data),
    onSuccess: (analysis) => {
      navigate(`/analyses/${analysis.id}`);
    },
  });

  const validateEtsyUrl = (value: string): boolean => {
    try {
      const parsed = new URL(value);
      if (!parsed.hostname.includes("etsy.com")) {
        setUrlError("Bu bir Etsy URL'si değil");
        return false;
      }
      setUrlError(null);
      return true;
    } catch {
      setUrlError("Geçerli bir URL giriniz");
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEtsyUrl(url)) return;

    mutation.mutate({ url, imageUrls: [], tags: [] });
  };

  return (
    <div className="mx-auto max-w-2xl p-8">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Yeni Analiz</h2>
        <p className="mt-1 text-gray-500">
          Bir Etsy ürün sayfası URL'si gir ve AI'nın analiz etmesine izin ver.
        </p>
      </div>

      {/* How it works */}
      <div className="mb-6 rounded-xl border border-indigo-100 bg-indigo-50 p-5">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-500" />
          <div>
            <p className="text-sm font-semibold text-indigo-800 mb-2">
              Nasıl Çalışır?
            </p>
            <ol className="space-y-1.5 text-sm text-indigo-700">
              <li>1. Chrome extension ile Etsy sayfasında "Analyze Listing" butonuna bas</li>
              <li>2. Extension verileri otomatik toplar ve buraya gönderir</li>
              <li>3. Ya da URL'yi doğrudan aşağıya yapıştır</li>
              <li>4. AI analiz ederek özgün brief'ler ve listing taslakları üretir</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Etsy Ürün URL'si
            </label>
            <div className="relative">
              <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (urlError) validateEtsyUrl(e.target.value);
                }}
                placeholder="https://www.etsy.com/listing/..."
                required
                className={`w-full rounded-lg border py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${
                  urlError ? "border-red-300" : "border-gray-300"
                }`}
              />
            </div>
            {urlError && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                <p className="text-xs text-red-600">{urlError}</p>
              </div>
            )}
            <p className="mt-1.5 text-xs text-gray-500">
              Örnek: https://www.etsy.com/listing/1234567890/...
            </p>
          </div>

          {mutation.error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5">
              <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500" />
              <p className="text-sm text-red-700">
                {(mutation.error as Error).message}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={mutation.isPending || !url}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 py-3 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
          >
            {mutation.isPending ? (
              <>
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Analiz ediliyor...
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                Analiz Başlat
              </>
            )}
          </button>
        </form>
      </div>

      {/* Extension promo */}
      <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-5">
        <p className="text-sm font-semibold text-gray-700 mb-2">
          Chrome Extension ile Daha Hızlı
        </p>
        <p className="text-sm text-gray-600">
          Extension'ı yükle, herhangi bir Etsy ürün sayfasında "Analyze" butonuna
          bas — veriler otomatik toplanır ve analize gönderilir.
        </p>
        <p className="mt-2 text-xs text-gray-400">
          Kurulum: Chrome Extensions → Load Unpacked → apps/extension/dist
        </p>
      </div>
    </div>
  );
}
